import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { IUserAvatar } from '@interfaces/avatar';
import { ExperienceFeatures, IEvent } from '@interfaces/event';
import { APP_LOGO } from '@components/AppLogo/interfaces';
import { DashboardButtonEnum } from '@constants/dashboard';
import { isEventFeatureEnabled, processMandatoryEvents } from '@helpers/events';
import { useUserProfileHelper } from '@hooks/clientSpecificHooks';
import { CHAT_DRAWER_WIDTH, MINUTES_ALLOW_TO_JOIN } from '@constants';

import { env } from 'env';

import {
  isBroadcastEnabled as isBroadcastEnabledEk,
  isPrivateChatEnabled as isPrivateChatEnabledEk,
} from '@clients/ek/helpers';
import {
  clientSpecificAvatarDeletion,
  clientSpecificCanCreateNewAvatar,
  clientSpecificCustomTextDeleteEditActionsOnAvatar,
  clientSpecificDashboardButtonDisabled,
  clientSpecificDashboardTopBarRight,
  clientSpecificEventProvider,
  clientSpecificHasMultiplayerTag,
  clientSpecificHideAvaturnCreation,
  clientSpecificHideGenericAvaturnType,
  clientSpecificHideRevokeAvatarConsentLink,
  clientSpecificIsSD,
  clientSpecificJoinEventSchedulerMinToJoin,
  clientSpecificLogo,
  clientSpecificProfileUserEmail,
  clientSpecificProfileUserGender,
  clientSpecificProfileUserName,
  clientSpecificSDScreenShareButtonDisabled,
  clientSpecificUserAvatars,
  clientSpecificVirtualAssistant,
} from '@clients/ek/helpers/clientSpecificUtils';

const { REACT_APP_CLIENT_ENV, REACT_APP_TMS_CLASS_ROOMS_TAG } = env;

const reactTMSClassRoomsTag = REACT_APP_TMS_CLASS_ROOMS_TAG || 'ClassRooms';

export const useClientSpecificProfileInformation = () => {
  const { user } = useMytaverse();

  const profileUserName = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return clientSpecificProfileUserName(user);
      default:
        return <Typography>{user.name}</Typography>;
    }
  }, [user]);

  const profileEmail = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK': {
        return clientSpecificProfileUserEmail(user);
      }
      default:
        return user.email;
    }
  }, [user]);

  const profileGender = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK': {
        return clientSpecificProfileUserGender(user);
      }
      default:
        return '';
    }
  }, [user]);

  return { profileUserName, profileEmail, profileGender };
};

export const useClientSpecificVirtualAssistant = () => {
  const { t: translate } = useTranslation();

  const { currentEvent } = useMytaverseEvent();

  return useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return clientSpecificVirtualAssistant({
          event: currentEvent,
          translate,
        });

      default:
        return null;
    }
  }, [currentEvent?.eventTags]);
};

export const useClientSpecificHasMultiplayerTag = () => {
  const { currentEvent } = useMytaverseEvent();

  const hasMultiplayerTag = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return clientSpecificHasMultiplayerTag(currentEvent?.eventTags);

      default:
        return false;
    }
  }, [currentEvent?.eventTags]);

  return { hasMultiplayerTag };
};

export const useClientSpecificPrivateChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();
  const { userPermissions } = useMytaverse();
  const { hasMultiplayerTag } = useClientSpecificHasMultiplayerTag();

  const isPrivateChatEnabled = useMemo(() => {
    if (!isEventFeatureEnabled(currentEvent, ExperienceFeatures.PrivateChat)) {
      return false;
    }

    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return (
          isPrivateChatEnabledEk({ currentEvent, userPermissions }) &&
          hasMultiplayerTag
        );
      default:
        return true;
    }
  }, [userPermissions, currentEvent, isPrivateChatEnabledEk]);

  return { isPrivateChatEnabled };
};

export const useClientSpecificBroadcastChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isBroadcastEnabled = useMemo(() => {
    if (!isEventFeatureEnabled(currentEvent, ExperienceFeatures.Broadcast)) {
      return false;
    }

    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return currentEvent?.eventTags?.includes(reactTMSClassRoomsTag);
      default:
        return true;
    }
  }, [currentEvent]);

  return { isBroadcastEnabled };
};

export const useClientSpecificGlobalChatVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isGlobalChatEnabled = useMemo(() => {
    return isEventFeatureEnabled(currentEvent, ExperienceFeatures.GlobalChat);
  }, [currentEvent]);

  return { isGlobalChatEnabled };
};

export const useClientSpecificCameraVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isCameraEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Camera),
    [currentEvent],
  );

  return { isCameraEnabled };
};

export const useClientSpecificMicVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isMicEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Mic),
    [currentEvent],
  );

  return { isMicEnabled };
};

export const useClientSpecificParticipantsVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isParticipantsEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Participants),
    [currentEvent],
  );

  return { isParticipantsEnabled };
};

export const useClientSpecificOnlineUsersVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isOnlineUsersEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.OnlineUsers),
    [currentEvent],
  );

  return { isOnlineUsersEnabled };
};

export const useClientSpecificTeleportVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isTeleportFeatureEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Teleport),
    [currentEvent],
  );

  return { isTeleportFeatureEnabled };
};

export const useClientSpecificLaserPointerVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isLaserPointerEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.LaserPointer),
    [currentEvent],
  );

  return { isLaserPointerEnabled };
};

export const useClientSpecificMagnifierVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isMagnifierEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Magnifier),
    [currentEvent],
  );

  return { isMagnifierEnabled };
};

export const useClientSpecificEmojisVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isEmojisEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Emojis),
    [currentEvent],
  );

  return { isEmojisEnabled };
};

export const useClientSpecificReactionsVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isReactionsEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Reactions),
    [currentEvent],
  );

  return { isReactionsEnabled };
};

export const useClientSpecificMapVisibility = () => {
  const { currentEvent } = useMytaverseEvent();

  const isMapEnabled = useMemo(
    () => isEventFeatureEnabled(currentEvent, ExperienceFeatures.Map),
    [currentEvent],
  );

  return { isMapEnabled };
};

export const useClientSpecificBroadcastChatInputVisibility = () => {
  const { userPermissions } = useMytaverse();

  const canBroadcast = useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK':
        return isBroadcastEnabledEk(userPermissions);
      default:
        return true;
    }
  }, [userPermissions, isBroadcastEnabledEk]);

  return { canBroadcast };
};

export const useClientSpecificChatDrawerWidth = () => {
  const { isPrivateChatEnabled } = useClientSpecificPrivateChatVisibility();
  const { isBroadcastEnabled } = useClientSpecificBroadcastChatVisibility();

  if (isPrivateChatEnabled && isBroadcastEnabled) {
    return { chatDrawerWidth: CHAT_DRAWER_WIDTH.THREE_TABS };
  }

  return { chatDrawerWidth: CHAT_DRAWER_WIDTH.TWO_TABS };
};

export const useClientSpecificCarouselEventsTransform = () => {
  const { appSettings } = useMytaverse();

  return useCallback(
    (events: IEvent[]) => {
      if (!events?.length) {
        return events;
      }

      switch (REACT_APP_CLIENT_ENV) {
        case 'EK': {
          return processMandatoryEvents(events, appSettings);
        }

        default:
          return events;
      }
    },
    [appSettings],
  );
};

export const useClientSpecificJoinEventSchedulerMinToJoin = () => {
  const { user } = useMytaverse();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK':
      return clientSpecificJoinEventSchedulerMinToJoin(user);
    default:
      return MINUTES_ALLOW_TO_JOIN;
  }
};

export const useClientSpecificCanCreateNewAvatar = (
  listOfAvatarsAlreadyCreated: IUserAvatar[],
): boolean => {
  const { getProcessedBrand } = useUserProfileHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificCanCreateNewAvatar({
        brand: getProcessedBrand(),
        listOfAvatarsAlreadyCreated,
      });
    }
    default:
      return (
        listOfAvatarsAlreadyCreated.length <
        parseInt(env.REACT_APP_MAX_NUMBER_OF_AVATARS || '3')
      );
  }
};

export const useClientSpecificCustomTextDeleteEditActionsOnAvatar = (
  avatar: IUserAvatar | null,
) => {
  const { getProcessedBrand } = useUserProfileHelper();
  let canEdit = true,
    canDelete = true,
    cannotEditAvatarNotification,
    customDeleteAvatarTitle = '',
    customDeleteAvatarText = '';

  if (avatar) {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK': {
        return clientSpecificCustomTextDeleteEditActionsOnAvatar({
          avatar,
          brand: getProcessedBrand(),
        });
      }
    }
  }

  return {
    canEdit,
    canDelete,
    cannotEditAvatarNotification,
    customDeleteAvatarTitle,
    customDeleteAvatarText,
  };
};

export const useClientSpecificHideGenericAvaturnType = () => {
  const { getProcessedBrand } = useUserProfileHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificHideGenericAvaturnType(getProcessedBrand());
    }
    default:
      return false;
  }
};

export const useClientSpecificHideRevokeAvatarConsentLink = () => {
  const { getProcessedBrand } = useUserProfileHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      const brand = getProcessedBrand();
      return clientSpecificHideRevokeAvatarConsentLink(brand);
    }
    default:
      return false;
  }
};

export const useClientSpecificHideAvaturnCreation = () => {
  const { user } = useMytaverse();

  const { getProcessedBrand } = useUserProfileHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificHideAvaturnCreation({
        user,
        brand: getProcessedBrand(),
      });
    }
    default:
      return false;
  }
};

export const useClientSpecificHideAvatarPolicyModal = () => {
  const { getProcessedBrand } = useUserProfileHelper();
  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificIsSD(getProcessedBrand());
    }
    default:
      return false;
  }
};

export const useClientSpecificNoEventsNotification = () => {
  const { getProcessedBrand } = useUserProfileHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return !clientSpecificIsSD(getProcessedBrand());
    }
    default:
      return false;
  }
};

export const useClientSpecificAvatarDeletion = () => {
  const { user, sendMessageToUnreal } = useMytaverse();

  const {
    currentEventParticipant,
    currentEvent,
    setCurrentEventParticipant,
    events,
  } = useMytaverseEvent();

  return useCallback(
    async (avatar: IUserAvatar) => {
      switch (REACT_APP_CLIENT_ENV) {
        case 'EK': {
          return clientSpecificAvatarDeletion({
            events,
            user,
            avatar,
            currentEventId: currentEvent?.eventId,
            currentEventParticipant,
            sendMessageToUnreal,
            setCurrentEventParticipant,
          });
        }
        default:
          return;
      }
    },
    [
      user,
      currentEventParticipant,
      currentEvent?.eventId,
      sendMessageToUnreal,
      events,
    ],
  );
};

export const useClientSpecificSDScreenShareButtonDisabled = () => {
  const { currentEventParticipant } = useMytaverseEvent();

  const { getProcessedBrand } = useUserProfileHelper();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificSDScreenShareButtonDisabled({
        brand: getProcessedBrand(),
        eventRole: currentEventParticipant?.role,
      });
    }

    default:
      return false;
  }
};

export const useClientSpecificLogo = () => {
  const { user } = useMytaverse();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      return clientSpecificLogo(user);
    }
    default:
      return {
        logo: APP_LOGO.DEFAULT,
        shouldStickToTheTop: false,
      };
  }
};

export const useClientSpecificDashboardButtonDisabled = (
  button: DashboardButtonEnum,
) => {
  const { familiarizeState } = useMytaverseEvent();

  switch (REACT_APP_CLIENT_ENV) {
    case 'EK': {
      if (!familiarizeState) {
        return false;
      }

      return clientSpecificDashboardButtonDisabled({
        buttonName: button,
        familiarizeState: familiarizeState.name,
      });
    }
    default:
      return false;
  }
};

export const useClientSpecificUserAvatars = (avatars: IUserAvatar[]) => {
  const { currentEventParticipant } = useMytaverseEvent();

  const { getProcessedBrand, getGender } = useUserProfileHelper();

  return useMemo(() => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK': {
        return clientSpecificUserAvatars({
          role: currentEventParticipant?.role || '',
          brand: getProcessedBrand(),
          gender: getGender() || '',
          avatars,
        });
      }
      default:
        return avatars;
    }
  }, [currentEventParticipant?.role, avatars]);
};

export const useClientSpecificEventProvider = () => {
  return (Component: JSX.Element) => {
    switch (REACT_APP_CLIENT_ENV) {
      case 'EK': {
        return clientSpecificEventProvider(Component);
      }
      default:
        return Component;
    }
  };
};
